import { isMultiValueSubfield } from './utils/multiValueSubfield';
/**
 * NOTE (CZNA-487): the flat `FormState` shape — a fixed record of `familyName`,
 * `mainHeading`, `identifiers[]`, … — was the state model of `LegacyForm`, the
 * hardcoded second form engine. It was deleted with that engine. The single
 * remaining model is {@link GenericFormState}: `fields[tag][instance][subfield]`,
 * built from the compiled form config by `getDefaultGenericFormState` below.
 */
/**
 * Returns default indicator values for a FieldDef.
 */
export var getDefaultIndicators = function (fieldDef) {
    var _a, _b, _c, _d, _e, _f;
    return [
        (_c = (_b = (_a = fieldDef.indicator1Def) === null || _a === void 0 ? void 0 : _a.defaultValue) !== null && _b !== void 0 ? _b : fieldDef.indicator1) !== null && _c !== void 0 ? _c : ' ',
        (_f = (_e = (_d = fieldDef.indicator2Def) === null || _d === void 0 ? void 0 : _d.defaultValue) !== null && _e !== void 0 ? _e : fieldDef.indicator2) !== null && _f !== void 0 ? _f : ' ',
    ];
};
// `toLocaleDateString('en-CA')` returns YYYY-MM-DD in the runtime's local TZ
// (cataloger's day, not UTC). Avoids "tomorrow already" prefill late evening.
export var getTodayIso = function () { return new Date().toLocaleDateString('en-CA'); };
/**
 * Today's date formatted in the Czech Aleph cataloging convention
 * (`D. M. YYYY`, no leading zeros, regular spaces). Matches the
 * dominant historical pattern in NKP field 670 citations
 * (98.72% of 1.23M `cit.` rows per 2026-05-14 audit — see TASK-124).
 *
 * `Intl.DateTimeFormat('cs-CZ')` emits non-breaking spaces (U+00A0)
 * between components; we normalize to regular space (U+0020) so the
 * output matches existing data byte-for-byte (search/replace, diffs).
 */
export var getTodayCs = function () {
    var formatted = new Intl.DateTimeFormat('cs-CZ', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
    }).format(new Date());
    // Replace any non-breaking spaces (U+00A0) with regular spaces (U+0020).
    return formatted.replace(/\u00a0/g, ' ');
};
/**
 * The state of one subfield that holds nothing: no value, empty components, no
 * linked record, no multi values. CZNA-1515 — also what removing an optional
 * subfield from the form writes, so a removed row and a never-filled one are
 * the same state, and the save drops both.
 */
export var emptySubfieldValue = function (sf) {
    var subVal = { value: '' };
    if (sf.hasComponents && sf.components) {
        subVal.components = {};
        for (var _i = 0, _a = sf.components; _i < _a.length; _i++) {
            var comp = _a[_i];
            subVal.components[comp.componentType] = '';
        }
    }
    if (sf.inputType === 'record-picker' || sf.inputType === 'vocabulary') {
        subVal.sourceAuthorityId = '';
    }
    // Multi-value subfields (TASK-064) start with an empty `values` array.
    if (isMultiValueSubfield(sf)) {
        subVal.values = [];
    }
    return subVal;
};
/**
 * CZNA-1646 — the `values[]` a repeatable subfield starts with: its default as the one value
 * (with the default's link), or none.
 */
export var multiValueDefault = function (sf) {
    if (!sf.defaultValue)
        return [];
    return [sf.defaultValueSourceAuthorityId
            ? { value: sf.defaultValue, sourceAuthorityId: sf.defaultValueSourceAuthorityId }
            : { value: sf.defaultValue }];
};
/**
 * Creates an empty field instance from a FieldDef, including default indicator values.
 */
export var createEmptyFieldInstance = function (fieldDef) {
    var _a;
    var instance = {};
    var _b = getDefaultIndicators(fieldDef), ind1 = _b[0], ind2 = _b[1];
    instance._ind1 = { value: ind1 };
    instance._ind2 = { value: ind2 };
    for (var _i = 0, _c = fieldDef.subfields; _i < _c.length; _i++) {
        var sf = _c[_i];
        var subVal = emptySubfieldValue(sf);
        subVal.value = sf.defaultValue || '';
        if (isMultiValueSubfield(sf)) {
            // CZNA-1646 — a repeatable subfield holds its values in `values[]` and the save reads
            // nothing else, so a default left in `value` would be dropped: it is the first value,
            // with its link when the default is a picked record (CZNA-531).
            subVal.values = multiValueDefault(sf);
        }
        else if (sf.inputType === 'record-picker' || sf.inputType === 'vocabulary') {
            // CZNA-531: a default on a record picker is a reference, so it seeds the
            // FK too — otherwise the pre-filled heading links to nothing. (Since
            // CZNA-798 such a heading at least RENDERS, as an unlinked option; before
            // that the picker showed only the option matching `sourceAuthorityId`.)
            subVal.sourceAuthorityId = (_a = sf.defaultValueSourceAuthorityId) !== null && _a !== void 0 ? _a : '';
        }
        instance[sf.code] = subVal;
    }
    return instance;
};
/**
 * Creates default GenericFormState from a FormConfig.
 * Non-repeatable fields get one empty instance.
 * Repeatable fields get minInstances empty instances (default 0).
 */
export var getDefaultGenericFormState = function (config) {
    var fields = {};
    for (var _i = 0, _a = config.tabs; _i < _a.length; _i++) {
        var tab = _a[_i];
        var _loop_1 = function (fieldDef) {
            var key = fieldDef.fieldId || fieldDef.tag;
            if (fieldDef.repeatable) {
                var count = fieldDef.minInstances || 0;
                fields[key] = Array.from({ length: count }, function () { return createEmptyFieldInstance(fieldDef); });
            }
            else {
                fields[key] = [createEmptyFieldInstance(fieldDef)];
            }
        };
        for (var _b = 0, _c = tab.fields; _b < _c.length; _b++) {
            var fieldDef = _c[_b];
            _loop_1(fieldDef);
        }
    }
    return { fields: fields };
};
